import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { store } from "../../app/store/store";
import { setIsLoading } from "../common/commonSlice";
import { checkPermission, showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import { Localize } from "../../app/lang/langs";
import { act } from "react";

const initialState = {
    lastNewsId: null,
    items: [
        {
            x: 0,
            y: 0,
            w: 12,
            h: 3.2,
            i: "1",
            minW: 3,
            minH: 3,
            maxW: 12,
            maxH: 6,
            data: { type: "NEWS", title: "Actualités" },
            active: true
        },
        {
            x: 0,
            y: 1,
            w: 5,
            h: 3.3,
            i: "2",
            minW: 3,
            minH: 3,
            maxW: 12,
            maxH: 6,
            data: { type: "VCOMPPARTICIPATE", title: "Vcomp Participate" },
            active: true
        },
        {
            x: 5,
            y: 1,
            w: 7,
            h: 3.3,
            i: "3",
            minW: 3,
            minH: 3,
            maxW: 12,
            maxH: 6,
            data: { type: "VCOMPMANAGE", title: "Vcomp Manage" },
            active: true
        },
        {
            x: 0,
            y: 4,
            w: 2,
            h: 1,
            i: "4",
            minW: 2,
            minH: 1,
            maxW: 3,
            maxH: 1,
            data: { type: "LEAVES", title: Localize('PENDING_LEAVES'), value: 222, color: "var(--green-500)" },
            active: false
        }
    ],
    news: [],
    // The audience choices the composer offers: All, Collabs, then one entry per manager
    // profile. Fetched from the backend rather than built here — the profile list is data,
    // and the route is behind `news` EDIT, so only an author ever loads it.
    newsAudienceOptions: [],
    vcompParticipateNotification: [],
    vcompManageNotification: [],
    pendingLeaves: 0
}

export const DashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
        hideDashboardItem: (state, action) => {
            state.items = state.items.filter(item => item.i != action.payload)
        },
        setNewsList: (state, action) => {
            state.news = action.payload
        },
        // Read acknowledgement is irreversible: once a collab has confirmed a
        // news item, `seen` is never cleared again on the client either.
        markNewsSeen: (state, action) => {
            const { newsId, seen } = action.payload
            state.news = (state.news || []).map(news =>
                news.id === newsId && !news.seen ? { ...news, seen } : news
            )
        },
        setPendingLeaves: (state, action) => {
            state.pendingLeaves = action.payload
        },
        // The feed is only ever refetched for news NEWER than lastNewsId, so an attachment
        // added to or removed from a post already in the list would otherwise never show up
        // until a full reload. Both writers patch the item in place instead.
        setNewsAttachments: (state, action) => {
            const { newsId, attachments } = action.payload
            state.news = (state.news || []).map(news =>
                news.id === newsId ? { ...news, attachments: attachments || [] } : news
            )
        },
        removeNewsAttachment: (state, action) => {
            const { newsId, attachmentId } = action.payload
            state.news = (state.news || []).map(news =>
                news.id === newsId
                    ? { ...news, attachments: (news.attachments || []).filter(a => a.id !== attachmentId) }
                    : news
            )
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getDashboardData.fulfilled, (state, action) => {
            if (
                action.payload.responseData &&
                action.payload.responseData["news"]
            ) {
                if (!state.lastNewsId)
                    state.news = []
                let lastId
                action.payload.responseData["news"].map(n => {
                    if (!lastId || n.id > lastId) lastId = n.id
                    if (!state.lastNewsId) {
                        state.news.push({
                            ...n,
                            created_at: n["created_at"] && new Date(n["created_at"]).getTime()
                        })
                    } else {
                        state.news.unshift({
                            ...n,
                            created_at: n["created_at"] && new Date(n["created_at"]).getTime()
                        })
                    }

                })
                if(typeof lastId != 'undefined') state.lastNewsId = lastId
            }
        })
        builder.addCase(getVcompNotifications.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status
            ) {
                state.vcompParticipateNotification = action.payload.responseData.participate_projects || []
                state.vcompManageNotification = action.payload.responseData.managed_projects || []
            }
        })
        builder.addCase(getNewsAudienceOptions.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status) {
                state.newsAudienceOptions = action.payload.responseData.options || []
            }
        })
        builder.addCase(deleteNews.fulfilled, (state, action) => {
            if (action.payload.responseData && action.payload.responseData.status
            ) {
                showSuccessToast(Localize("NEWS_DELETED_SUCCESSFULLY"))
            }
        })
    }
})

export const getDashboardData = createAsyncThunk(
    "dashboard/getCollabData",
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.getCollabData,
                body
            )
            return response
        } catch (error) {
            return error
        }
    }
)
export const getVcompNotifications = createAsyncThunk(
    "dashboard/getVcompNotifications",
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.getVcompNotifications,
                body
            )
            return response
        } catch (error) {
            return error
        }
    }
)
export const markNewsAsRead = createAsyncThunk(
    "dashboard/markNewsAsRead",
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.markNewsAsRead,
                body
            )
            return response
        } catch (error) {
            return error
        }
    }
)
export const deleteNews = createAsyncThunk(
    "dashboard/deleteNews",
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.deleteNews,
                body
            )
            return response
        } catch (error) {
            return error
        }
    }
)
export const getNewsViewers = createAsyncThunk(
    "dashboard/getNewsViewers",
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.getNewsViewers,
                body
            )
            return response
        } catch (error) {
            return error
        }
    }
)
/**
 * The audience targets the composer can pick from. Kept in the store rather than fetched
 * per dialog: the list changes only when manager profiles do, and the add-news modal is
 * opened repeatedly.
 */
export const getNewsAudienceOptions = createAsyncThunk(
    "dashboard/getNewsAudienceOptions",
    async () => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.getNewsAudienceOptions,
                {}
            )
            return response
        } catch (error) {
            return error
        }
    }
)
export const addNews = createAsyncThunk(
    "dashboard/addNews",
    async (body) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.addNews,
                body
            )
            return response
        } catch (error) {
            return error
        }
    }
)
export const getNewsAttachments = createAsyncThunk(
    "dashboard/getNewsAttachments",
    async (newsId, { dispatch }) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.listNewsAttachments,
                { id_news: newsId }
            )
            if (response && response.responseData && response.responseData.status) {
                dispatch(setNewsAttachments({ newsId, attachments: response.responseData.list }))
            }
            return response
        } catch (error) {
            return { responseError: error }
        }
    }
)

/**
 * Uploads one file onto a news post.
 *
 * Not through postRequest: this one is multipart, so it goes via the dedicated
 * NetworkManager helper. The caller uploads its files one after another rather than in
 * parallel — the backend counts the existing rows before accepting a file, and concurrent
 * uploads would each read the count before any of them has written.
 */
export const uploadNewsAttachment = createAsyncThunk(
    "dashboard/uploadNewsAttachment",
    async ({ file, newsId }) => {
        try {
            const data = await NetworkManager.getInstance().uploadNewsAttachment(file, newsId)
            if (data && data.status == 1) return { ok: true, id: data.id }
            showErrorToast((data && data.error) || Localize('ATTACHMENT_UPLOAD_FAILED'))
            return { ok: false }
        } catch (error) {
            const message = error && error.response && error.response.data && error.response.data.error
            showErrorToast(message || Localize('ATTACHMENT_UPLOAD_FAILED'))
            return { ok: false }
        }
    }
)

export const deleteNewsAttachment = createAsyncThunk(
    "dashboard/deleteNewsAttachment",
    async ({ id, newsId }, { dispatch }) => {
        try {
            let response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.dashboard.deleteNewsAttachment,
                { id }
            )
            if (response && response.responseData && response.responseData.status) {
                dispatch(removeNewsAttachment({ newsId, attachmentId: id }))
            }
            return response
        } catch (error) {
            return { responseError: error }
        }
    }
)

export const {
    hideDashboardItem,
    setNewsList,
    markNewsSeen,
    setPendingLeaves,
    setNewsAttachments,
    removeNewsAttachment
} = DashboardSlice.actions



export default DashboardSlice.reducer